import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Copy, Download, ExternalLink, MessageCircle, QrCode, Smartphone, Apple, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import InstallQrCode, { createQrDataUrl } from '../components/InstallQrCode';
import { getInstallUrl } from '../lib/pwa';

function buildWhatsAppMessage(url: string) {
  return [
    '*Instale o app Cifrão no seu celular*',
    '',
    `1. Abra este link: ${url}`,
    '2. Android: toque em "Instalar app".',
    '3. iPhone: toque em Compartilhar e depois em "Adicionar à Tela de Início".',
    '',
    'Depois é só entrar com seu login de sempre.',
  ].join('\n');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    img.src = src;
  });
}

async function buildPrintableQr(url: string): Promise<string> {
  const width = 1080;
  const height = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponível');

  const [icon, qr] = await Promise.all([
    loadImage('/icons/icon-512.png'),
    createQrDataUrl(url, 720).then(loadImage),
  ]);

  ctx.fillStyle = '#0f1b2d';
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(80, 300, width - 160, 940, 48);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(width / 2 - 90, 80, 180, 180, 44);
  ctx.clip();
  ctx.drawImage(icon, width / 2 - 90, 80, 180, 180);
  ctx.restore();

  ctx.textAlign = 'center';
  ctx.fillStyle = '#0f1b2d';
  ctx.font = '700 64px system-ui, -apple-system, Segoe UI, sans-serif';
  ctx.fillText('Cifrão', width / 2, 400);
  ctx.fillStyle = '#6b7280';
  ctx.font = '400 34px system-ui, -apple-system, Segoe UI, sans-serif';
  ctx.fillText('Aponte a câmera do celular para instalar', width / 2, 455);

  ctx.drawImage(qr, width / 2 - 330, 490, 660, 660);

  ctx.fillStyle = '#b7791f';
  ctx.font = '600 30px system-ui, -apple-system, Segoe UI, sans-serif';
  ctx.fillText(url.replace(/^https?:\/\//, ''), width / 2, 1195);

  return canvas.toDataURL('image/png');
}

export default function AppCifrao() {
  const { user } = useAuth();
  const installUrl = getInstallUrl();
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const handleCopy = async () => {
    setError(null);
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Não foi possível copiar automaticamente. Selecione o link e copie manualmente.');
    }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(buildWhatsAppMessage(installUrl));
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async () => {
    setError(null);
    setDownloading(true);
    try {
      const dataUrl = await buildPrintableQr(installUrl);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'qrcode-app-cifrao.png';
      link.click();
    } catch {
      setError('Não foi possível gerar a imagem do QR Code. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">App Cifrão</h1>
        <p className="mt-1 text-gray-600">Compartilhe o link ou o QR Code para a equipe instalar o app no celular.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f1b2d] via-[#16263d] to-[#1d3150] p-6 text-white shadow-xl sm:p-8">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative flex flex-col items-center text-center">
            <div className="flex items-center gap-3">
              <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-xl ring-2 ring-white/10" />
              <div className="text-left">
                <p className="text-lg font-semibold leading-tight">Cifrão</p>
                <p className="text-xs text-slate-300">Escaneie para instalar</p>
              </div>
            </div>
            <div className="mt-6 rounded-3xl bg-white p-3 shadow-2xl">
              <InstallQrCode value={installUrl} size={220} />
            </div>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 transition-colors hover:bg-white/20 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              <Download className="h-4 w-4" />
              {downloading ? 'Gerando imagem...' : 'Baixar QR Code'}
            </button>
          </div>
        </section>

        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <QrCode className="h-5 w-5 text-blue-600" />
              Link de instalação
            </h2>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 pl-4">
              <input
                readOnly
                value={installUrl}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Link de instalação"
                className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none"
              />
              <button
                onClick={handleCopy}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${copied ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copiado' : 'Copiar link'}
              </button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <button
                onClick={handleWhatsApp}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                <MessageCircle className="h-4 w-4" />
                Enviar pelo WhatsApp
              </button>
              <a
                href={installUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Ver página de instalação
              </a>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">Como a equipe instala</h2>
            <div className="mt-4 space-y-4">
              <div className="flex gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                  <Smartphone className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Android</p>
                  <p className="text-sm leading-relaxed text-gray-600">Abre o link no Chrome e toca em "Instalar app".</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <Apple className="h-4 w-4 text-gray-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">iPhone</p>
                  <p className="text-sm leading-relaxed text-gray-600">Abre o link no Safari, toca em Compartilhar e depois em "Adicionar à Tela de Início".</p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2 rounded-xl bg-blue-50 p-3 text-sm leading-relaxed text-blue-800">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              O app abre na tela de login. Cada pessoa entra com o próprio usuário e vê apenas o que já pode ver hoje.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
